import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import { getAssignmentTitle } from '../../utils/assignmentUtils';
import '../../styles/ChildProfilePage.css';
import '../../styles/ChildAssignmentCardFix.css';

const ChildProfilePage = () => {
  const { childId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // State for child data and loading status
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [childParents, setChildParents] = useState([]);
  const [learningPlans, setLearningPlans] = useState([]);
  const [learningPlanLoading, setLearningPlanLoading] = useState(true);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [planAssignments, setPlanAssignments] = useState({});

  // State for child's data
  const [assignments, setAssignments] = useState({ active: [], completed: [] });
  const [stats, setStats] = useState({
    assignments: { completionRate: 0 },
    attendance: { rate: 0, present: 0, total: 0 },
    ratings: { average: 0 }
  });
  
  // Use childId from params or from location state
  const effectiveChildId = childId || location.state?.childId;
  const childName = location.state?.childName;
  
  // Fetch child details and progress data
  useEffect(() => {
    if (!effectiveChildId) {
      setError('No child ID provided');
      setLoading(false);
      return;
    }
    
    const fetchChildData = async () => {
      setLoading(true);
      console.log('ChildProfilePage: Fetching data for child ID:', effectiveChildId);
      
      try {
        // Fetch child details first
        const childResponse = await api.get(`/users/${effectiveChildId}`);
        console.log('ChildProfilePage: Child details response:', childResponse.data);
        
        if (childResponse.data) {
          setChild(childResponse.data);
        } else {
          console.warn('ChildProfilePage: No child data returned from API');
        }
        
        // Fetch assignments data
        console.log('ChildProfilePage: Fetching assignments for child');
        const assignmentsResponse = await api.get(`/parents/children/${effectiveChildId}/assignments`);
        console.log('ChildProfilePage: Assignments response:', assignmentsResponse.data);
        
        if (assignmentsResponse.data && assignmentsResponse.data.data) {
          const assignmentsData = assignmentsResponse.data.data;
          console.log(`ChildProfilePage: Found ${assignmentsData.length} assignments`);
          
          // Filter active assignments - ANY assignment that's active OR needs repeat
          const activeAssignments = assignmentsData.filter(assignment => 
            assignment.status === 'active' || 
            (assignment.assessment && assignment.assessment.rating === 'Need to Repeat')
          ).sort((a, b) => {
            // Sort by due date - latest due date first (furthest in future first)
            const dateA = new Date(a.dueDate || '1900-01-01');
            const dateB = new Date(b.dueDate || '1900-01-01');
            return dateB.getTime() - dateA.getTime();
          });
          
          // Filter completed assignments - ONLY assignments that are completed status AND don't need repeat
          const completedAssignments = assignmentsData.filter(assignment => 
            assignment.status === 'completed' && 
            (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
          ).slice(0, 5); // Only show 5 most recent completed assignments
          
          setAssignments({
            active: activeAssignments,
            completed: completedAssignments
          });
          
          // Calculate assignment metrics
          const totalAssignments = assignmentsData.length;
          const completedCount = assignmentsData.filter(assignment => 
            assignment.status === 'completed' &&
            (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
          ).length;
          
          const completionRate = totalAssignments > 0 
            ? Math.round((completedCount / totalAssignments) * 100) 
            : 0;
          
          // Calculate assessment average
          const assessedAssignments = assignmentsData.filter(assignment => 
            assignment.assessment && assignment.assessment.rating
          );
          
          let assessmentAvgValue = 0;
          
          if (assessedAssignments.length > 0) {
            // Map ratings to numerical values
            const ratingValues = {
              'Excellent': 5,
              'Very Good': 4,
              'Good': 3,
              'Pass': 2,
              'Need to Repeat': 1
            };
            
            const assessmentSum = assessedAssignments.reduce((sum, assignment) => {
              return sum + (ratingValues[assignment.assessment.rating] || 0);
            }, 0);
            
            assessmentAvgValue = assessmentSum / assessedAssignments.length;
          }
          
          // Update assignment stats
          setStats(prev => ({
            ...prev,
            assignments: { 
              completionRate,
              total: totalAssignments,
              completed: completedCount
            },
            ratings: { 
              average: assessmentAvgValue > 0 ? parseFloat(assessmentAvgValue.toFixed(1)) : 0
            }
          }));
        }
        
        // Fetch attendance data
        console.log('ChildProfilePage: Fetching attendance for child');
        const attendanceResponse = await api.get(`/parents/children/${effectiveChildId}/attendance`);
        console.log('ChildProfilePage: Attendance response:', attendanceResponse.data);
        
        if (attendanceResponse.data && attendanceResponse.data.stats) {
          const attendanceStats = attendanceResponse.data.stats;
          console.log('ChildProfilePage: Attendance stats:', attendanceStats);
          
          // Update attendance stats
          setStats(prev => ({
            ...prev,
            attendance: { 
              rate: attendanceStats.attendanceRate || 0,
              present: attendanceStats.presentDays || 0,
              total: attendanceStats.totalDays || 0
            }
          }));
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching child data:', err);
        setError('Failed to load child data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchChildData();
  }, [effectiveChildId]);

  // Fetch child's parents to show context
  useEffect(() => {
    const fetchChildParents = async () => {
      if (!effectiveChildId) return;

      try {
        const response = await api.get(`/students/${effectiveChildId}/parents`);
        setChildParents(response.data.data || []);
      } catch (error) {
        console.error('Error fetching child parents:', error);
      }
    };

    fetchChildParents();
  }, [effectiveChildId]);

  // Fetch child's active learning plans
  useEffect(() => {
    const fetchLearningPlans = async () => {
      if (!effectiveChildId) return;

      try {
        setLearningPlanLoading(true);
        const response = await api.get(`/learning-plans/student/${effectiveChildId}`);
        const plans = response.data.data || [];

        // Filter for active plans only
        const activePlans = plans.filter(plan => plan.status === 'active');

        // Enrich each plan with average rating and store assignments
        const allPlanAssignments = {};
        const enrichedPlans = await Promise.all(
          activePlans.map(async (plan) => {
            try {
              const assignmentsResponse = await api.get(`/learning-plans/${plan._id}/assignments`);
              const assignments = assignmentsResponse.data.data || [];

              // Sort assignments by due date (earliest first)
              const sortedAssignments = assignments.sort((a, b) => {
                const dateA = new Date(a.dueDate || '9999-12-31');
                const dateB = new Date(b.dueDate || '9999-12-31');
                return dateA - dateB;
              });

              // Accumulate assignments
              allPlanAssignments[plan._id] = sortedAssignments;

              // Calculate average rating (excluding "Need to Repeat")
              const getRatingValue = (rating) => {
                const ratingMap = {
                  'Excellent': 5,
                  'Very Good': 4,
                  'Good': 3,
                  'Pass': 2,
                  'Weak': 1
                };
                return ratingMap[rating] || 0;
              };

              const ratedAssignments = assignments.filter(a =>
                a.assessment &&
                a.assessment.rating &&
                a.assessment.rating !== 'Need to Repeat'
              );

              if (ratedAssignments.length > 0) {
                const totalRating = ratedAssignments.reduce((sum, assignment) => {
                  return sum + getRatingValue(assignment.assessment.rating);
                }, 0);

                plan.averageRating = (totalRating / ratedAssignments.length).toFixed(1);
                plan.ratedAssignmentsCount = ratedAssignments.length;
              }

              return plan;
            } catch (assignmentsError) {
              console.error('Error fetching plan assignments for rating:', assignmentsError);
              return plan;
            }
          })
        );

        // Set all assignments at once to avoid race conditions
        setPlanAssignments(allPlanAssignments);
        setLearningPlans(enrichedPlans);
      } catch (error) {
        console.error('Error fetching learning plans:', error);
        setLearningPlans([]);
      } finally {
        setLearningPlanLoading(false);
      }
    };

    fetchLearningPlans();
  }, [effectiveChildId]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'No due date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'No due date';
    }
  };
  
  // Navigate to assignment details
  const navigateToAssignment = (assignment) => {
    // Use the student-view route which has the appropriate permissions for parents and students
    navigate(`/assignments/student-view/${assignment._id}`, {
      state: {
        fromParentChildView: true,
        childId: effectiveChildId,
        childName: child?.name || childName
      }
    });
  };
  
  // Navigate to all assignments
  const viewAllAssignments = () => {
    // Check if child is a student (has student role)
    if (child?.role === 'student') {
      // Navigate to StudentAssignmentsListPage and pass the student ID
      navigate(`/assignments/student`, {
        state: {
          fromParentChildView: true,
          childId: effectiveChildId,
          childName: child?.name || childName
        }
      });
    } else {
      // Fall back to the parent-specific assignments page if not a student
      navigate(`/parent/children/${effectiveChildId}/assignments`, {
        state: {
          fromParentChildView: true,
          childId: effectiveChildId,
          childName: child?.name || childName
        }
      });
    }
  };

  // Toggle learning plan expansion
  const togglePlanExpansion = (planId) => {
    setExpandedPlanId(expandedPlanId === planId ? null : planId);
  };

  // Get short status label
  const getShortStatus = (assignment) => {
    if (assignment.status === 'completed') {
      if (assignment.assessment?.rating) {
        return assignment.assessment.rating;
      }
      return 'Completed';
    }
    return 'Pending';
  };

  // Format short date
  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' });
    } catch (error) {
      return '';
    }
  };
  
  // Navigate to attendance
  const viewAttendance = () => {
    navigate(`/parent/children/${effectiveChildId}/attendance`, {
      state: {
        childId: effectiveChildId,
        childName: child?.name || childName
      }
    });
  };
  
  if (loading) {
    return (
      <div className="child-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading child profile...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="child-profile-error">
        <p>{error}</p>
        <button onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </button>
      </div>
    );
  }
  
  const childDisplayName = child?.name || childName || 'Child';
  
  return (
    <div className="child-profile-container">
      <div className="child-profile-header">
        <Link to="/dashboard" className="back-link">
          ← Back to Parent Dashboard
        </Link>
        <div className="profile-title-section">
          <h1>{childDisplayName}'s Profile</h1>
          <div className="parent-context" style={{
            fontSize: '14px',
            color: '#666',
            marginTop: '8px'
          }}>
            <span>Viewing as: <strong>{user.name || 'Parent'}</strong></span>
            {childParents.length > 1 && (
              <div style={{
                marginTop: '4px',
                padding: '4px 8px',
                backgroundColor: '#e8f4fd',
                borderRadius: '12px',
                fontSize: '12px',
                display: 'inline-block'
              }}>
                👨‍👩‍👧‍👦 This child has {childParents.length} parents
                {childParents.filter(p => p._id !== user.id).length > 0 && (
                  <span style={{ marginLeft: '8px', color: '#4a90e2' }}>
                    (Other: {childParents.filter(p => p._id !== user.id).map(p => p.name).join(', ')})
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="child-stats-overview">
        <div className="stat-card assignments-stat">
          <h3>Assignments</h3>
          <div className="stat-value">{stats.assignments.completionRate}%</div>
          <div className="stat-details">
            {stats.assignments.completed} of {stats.assignments.total} completed
          </div>
        </div>

        <div className="stat-card ratings-stat">
          <h3>Average Rating</h3>
          <div className="stat-value">{stats.ratings.average}/5</div>
          <div className="stat-details">
            Based on {assignments.completed.length} assessed assignments
          </div>
        </div>

        <div className="stat-card attendance-stat">
          <h3>Attendance</h3>
          <div className="stat-value">{stats.attendance.rate}</div>
          <div className="stat-details">
            Present: {stats.attendance.present}/{stats.attendance.total} days
          </div>
        </div>
      </div>

      {/* Learning Plans Section */}
      {!learningPlanLoading && (
        <>
          <div className="section-header" style={{ marginTop: '30px' }}>
            <h2>Learning Plans {learningPlans.length > 0 && `(${learningPlans.length})`}</h2>
          </div>
          {learningPlans.length > 0 ? (
            learningPlans.map((learningPlan) => (
            <div key={learningPlan._id} style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              border: '1px solid #e0e0e0'
            }}>
              {/* Plan Header - Clickable to expand/collapse */}
              <div
                onClick={() => togglePlanExpansion(learningPlan._id)}
                style={{
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <h3 style={{ margin: '0', fontSize: '18px', color: '#333' }}>
                        {learningPlan.assignmentType === 'new-memorization' && 'New Memorization'}
                        {learningPlan.assignmentType === 'revision' && 'Revision'}
                        {learningPlan.assignmentType === 'recitation' && 'Recitation'}
                        {!learningPlan.assignmentType && 'Learning Plan'}
                      </h3>
                      {learningPlan.averageRating && (
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            backgroundColor: '#e3f2fd',
                            borderRadius: '12px'
                          }}
                          title={`Based on ${learningPlan.ratedAssignmentsCount || 0} assessed assignment${learningPlan.ratedAssignmentsCount !== 1 ? 's' : ''}`}
                        >
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1976d2' }}>
                            {learningPlan.averageRating}/5
                          </span>
                          <span style={{ fontSize: '11px', color: '#666' }}>
                            Avg
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#4a90e2' }}>
                      {learningPlan.progress?.percent || 0}%
                    </span>
                    <span style={{ fontSize: '18px', color: '#666' }}>
                      {expandedPlanId === learningPlan._id ? '▲' : '▼'}
                    </span>
                  </div>
                </div>
                {/* Progress bar - always visible */}
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: expandedPlanId === learningPlan._id ? '15px' : '0'
                }}>
                  <div style={{
                    width: `${learningPlan.progress?.percent || 0}%`,
                    height: '100%',
                    backgroundColor: learningPlan.progress?.percent >= 75 ? '#4caf50' : learningPlan.progress?.percent >= 50 ? '#2196f3' : learningPlan.progress?.percent >= 25 ? '#ff9800' : '#f44336',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {/* Expandable Details */}
              {expandedPlanId === learningPlan._id && (
              <>
              {/* Progress Details */}
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>Progress</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#888' }}>
                  <span>{learningPlan.progress?.completed || 0} of {learningPlan.progress?.total || 0} assignments completed</span>
                  <span>{(learningPlan.progress?.total || 0) - (learningPlan.progress?.completed || 0)} remaining</span>
                </div>

                {/* Schedule Status */}
                {(() => {
                  const overdue = learningPlan.progress?.due || 0;
                  const scheduleStatus = overdue === 0
                    ? { text: 'On Track', icon: '✓', color: '#4caf50' }
                    : overdue >= 3
                    ? { text: 'Behind Schedule', icon: '⚠', color: '#f44336' }
                    : { text: 'Slightly Behind', icon: '⚠', color: '#ff9800' };

                  return (
                    <div style={{
                      marginTop: '10px',
                      padding: '8px 10px',
                      backgroundColor: scheduleStatus.color === '#4caf50' ? '#e8f5e9' : scheduleStatus.color === '#ff9800' ? '#fff3e0' : '#ffebee',
                      borderLeft: `3px solid ${scheduleStatus.color}`,
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: scheduleStatus.color, fontWeight: '600' }}>
                        <span>{scheduleStatus.icon}</span>
                        <span>Schedule: {scheduleStatus.text}</span>
                      </div>
                      {overdue > 0 && (
                        <div style={{ marginTop: '2px', fontSize: '12px', color: '#666' }}>
                          {overdue} overdue assignment{overdue !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Schedule Info */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '3px' }}>Schedule</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {learningPlan.schedule.daysPerWeek} days/week
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '3px' }}>Days</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {learningPlan.schedule?.specificDays?.map(d => d.substring(0, 3)).join(', ') || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '3px' }}>End Date</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {learningPlan.timeline?.end ? formatDate(learningPlan.timeline.end) : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Assignments Section */}
              {planAssignments[learningPlan._id] && planAssignments[learningPlan._id].length > 0 && (
                <div style={{ marginTop: '15px', borderTop: '1px solid #f0f0f0', paddingTop: '15px' }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '10px'
                  }}>
                    Assignments ({planAssignments[learningPlan._id].length})
                  </div>
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {planAssignments[learningPlan._id].map((assignment, index) => (
                      <div
                        key={assignment._id}
                        style={{
                          padding: '8px 10px',
                          marginBottom: '6px',
                          backgroundColor: '#f9f9f9',
                          borderRadius: '4px',
                          fontSize: '13px',
                          color: '#333',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '10px'
                        }}
                      >
                        <span style={{ flex: '1 1 auto', minWidth: 0 }}>
                          {getAssignmentTitle(assignment)}
                        </span>
                        <span style={{
                          flex: '0 0 auto',
                          fontSize: '12px',
                          color: assignment.status === 'completed' ? '#4caf50' : '#666',
                          fontWeight: '500'
                        }}>
                          {getShortStatus(assignment)}
                        </span>
                        {assignment.dueDate && (
                          <span style={{
                            flex: '0 0 auto',
                            fontSize: '12px',
                            color: '#888',
                            whiteSpace: 'nowrap'
                          }}>
                            {formatShortDate(assignment.dueDate)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
              )}
            </div>
            ))
          ) : (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '30px 20px',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              border: '1px solid #e0e0e0',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                No active learning plans
              </p>
            </div>
          )}
        </>
      )}

      {/* Current Assignments */}
      <div className="assignments-section">
        <div className="section-header">
          <h2>Current Assignments</h2>
          <button 
            className="view-all-btn"
            onClick={viewAllAssignments}
          >
            View All
          </button>
        </div>
        
        {assignments.active.length > 0 ? (
          <div className="assignments-list">
            {assignments.active.map(assignment => (
              <div 
                key={assignment._id} 
                className="assignment-card"
                onClick={() => navigateToAssignment(assignment)}
              >
                <div className={`assignment-type-badge type-${assignment.type}`}>
                  {assignment.type === 'new-memorization' ? 'New Memorization' :
                   assignment.type === 'revision' ? 'Revision' :
                   assignment.type === 'recitation' ? 'Recitation' : 'Unknown'}
                </div>
                {/* Make sure title is properly displayed on both desktop and mobile */}
                <h3 className="assignment-title">
                  {getAssignmentTitle(assignment)}
                </h3>
                <div className="assignment-due-date">
                  Due: {formatDate(assignment.dueDate)}
                </div>
                <div className={`assignment-status ${assignment.assessment ? 'has-assessment' : ''}`}>
                  {assignment.assessment && assignment.assessment.rating
                    ? `Assessment: ${assignment.assessment.rating}` 
                    : 'Status: Pending Assessment'}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-assignments">
            <p>No current assignments</p>
          </div>
        )}
      </div>
      
      
      {/* Attendance Summary */}
      <div className="attendance-section">
        <div className="section-header">
          <h2>Attendance Summary</h2>
          <button 
            className="view-all-btn"
            onClick={viewAttendance}
          >
            View Full Record
          </button>
        </div>
        
        <div className="attendance-summary">
          <div className="attendance-stats">
            <div className="attendance-stat">
              <span className="stat-label">Present:</span>
              <span className="stat-value">{stats.attendance.present} days</span>
            </div>
            <div className="attendance-stat">
              <span className="stat-label">Absent:</span>
              <span className="stat-value">{stats.attendance.total - stats.attendance.present} days</span>
            </div>
            <div className="attendance-stat">
              <span className="stat-label">Attendance Rate:</span>
              <span className="stat-value">{stats.attendance.rate}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildProfilePage;